/**
 * Dictation settings — which engine listens, through which microphone, and in
 * which language.
 *
 * Its own store rather than a field on `tts-settings-store`, which is about
 * speaking: the two share a Settings section but nothing else. A user who
 * clears their voice settings should not lose their dictation privacy choice
 * along with their preferred voice.
 *
 * Persisted to localStorage so the choice survives reloads, and mirrored across
 * windows so changing it in Settings applies to the recording started from the
 * cursor overlay.
 */
import { invoke } from "@tauri-apps/api/core";
import { create } from "zustand";

import type { AppLanguage } from "@/i18n";
import { isTauri } from "@/lib/is-tauri";
import { mirroredStore } from "@/lib/mirrored-store";
import {
  setDictationEnginePreference,
  setDictationLocalePreference,
} from "@/desktop/dictation-engine-preference";
import type { DictationEngineChoice } from "@/desktop/dictation-engine-options";
import { setVoiceInputDevicePreference } from "@/desktop/voice-input-preference";

const STORAGE_KEY = "denker.dictation-settings";

export type DictationSettings = {
  /**
   * Which engine to listen with, when the user has a choice.
   *
   * `null` means "whatever this machine defaults to"
   * (`denker_dictation_engine`). Windows and Apple Silicon Macs offer a real
   * choice: the platform recogniser streams words as they are spoken (on
   * Windows by sending the audio to Microsoft), while Whisper keeps everything
   * on the machine but cannot produce text until the user stops speaking. An
   * Intel Mac dictates with Whisper alone, whatever is stored here.
   */
  engine: DictationEngineChoice | null;
  /**
   * The microphone every capture opens — dictation, live voice and the wake
   * word — as the native device id. `null` means Automatic: the native side
   * picks the best input each time a capture starts.
   */
  inputDeviceId: string | null;
  /** The chosen device's name, kept only to label it while it is unplugged. */
  inputDeviceName: string | null;
  /**
   * The language every voice input is recognised in — dictation, live voice
   * and the input-bar microphones — as a BCP-47 tag, one of
   * `dictation_supported_locales`. `null` means the engine default, exactly
   * as before the choice existed. The wake word has no language.
   */
  locale: string | null;
  /**
   * The languages the user speaks, as tags of the Whisper language list:
   * while the language is Automatic, Whisper detects each recording's
   * language among these only. `null` means the app language and English
   * (`spokenLanguagesOrDefault`) until the app language is switched, which
   * saves the list shown then (`app-language-store.ts`).
   */
  spokenLanguages: string[] | null;
};

type InputDevice = Pick<DictationSettings, "inputDeviceId" | "inputDeviceName">;

type Actions = {
  setEngine: (engine: DictationEngineChoice | null) => void;
  setInputDevice: (device: { id: string; name: string } | null) => void;
  setLocale: (locale: string | null) => void;
  setSpokenLanguages: (languages: string[]) => void;
};

const AUTOMATIC_INPUT: InputDevice = {
  inputDeviceId: null,
  inputDeviceName: null,
};
const DEFAULTS: DictationSettings = {
  engine: null,
  ...AUTOMATIC_INPUT,
  locale: null,
  spokenLanguages: null,
};
const INPUT_DEVICE_TEXT_MAX = 512;
/** Whisper knows 99 languages; nobody speaks more. */
const SPOKEN_LANGUAGES_MAX = 99;
/** The shape the native start accepts: letters, digits and hyphens. */
const LOCALE_TAG = /^[A-Za-z0-9-]{1,35}$/;

function parseEngine(value: unknown): DictationEngineChoice | null {
  return value === "system" || value === "whisper" ? value : null;
}

function parseLocale(value: unknown): string | null {
  return typeof value === "string" && LOCALE_TAG.test(value) ? value : null;
}

/** Distinct well-formed tags, in order; none at all is the default. */
function parseSpokenLanguages(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const tags = [
    ...new Set(value.filter((tag): tag is string => parseLocale(tag) !== null)),
  ].slice(0, SPOKEN_LANGUAGES_MAX);
  return tags.length > 0 ? tags : null;
}

/** The languages detection prefers: the user's, else the app language
 *  and English - the default the native side applies too
 *  (`dictation_spoken_languages.rs`). */
export function spokenLanguagesOrDefault(
  saved: string[] | null,
  appLanguage: AppLanguage,
): string[] {
  return saved ?? [...new Set([appLanguage, "en"])];
}

function isInputDeviceText(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= INPUT_DEVICE_TEXT_MAX
  );
}

/** A device is its id and its name together; anything less is Automatic. */
function parseInputDevice(id: unknown, name: unknown): InputDevice {
  return isInputDeviceText(id) && isInputDeviceText(name)
    ? { inputDeviceId: id, inputDeviceName: name }
    : AUTOMATIC_INPUT;
}

function loadInitial(): DictationSettings {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const saved = JSON.parse(raw) as Partial<DictationSettings>;
    return {
      engine: parseEngine(saved.engine),
      ...parseInputDevice(saved.inputDeviceId, saved.inputDeviceName),
      locale: parseLocale(saved.locale),
      spokenLanguages: parseSpokenLanguages(saved.spokenLanguages),
    };
  } catch {
    return DEFAULTS;
  }
}

/** Mirror the choices into the modules the recording path reads synchronously.
 *  Wired to the store rather than to the action, so state arriving from another
 *  window lands here too. */
function publish(state: DictationSettings) {
  setDictationEnginePreference(state.engine);
  setVoiceInputDevicePreference(state.inputDeviceId);
  setDictationLocalePreference(state.locale);
  tellNativeSpokenLanguages(state.spokenLanguages);
}

let toldNative: string | undefined;

/** Whisper runs natively, so the languages go there: at start-up from every
 *  window, idempotently, and on every change. A call that fails is tried
 *  again when the window next gains focus, with the languages of then. */
function tellNativeSpokenLanguages(languages: string[] | null) {
  const told = JSON.stringify(languages);
  if (!isTauri() || told === toldNative) return;
  toldNative = told;
  void invoke("set_spoken_languages", { languages }).catch(() => {
    toldNative = undefined;
    window.addEventListener(
      "focus",
      () =>
        tellNativeSpokenLanguages(
          useDictationSettingsStore.getState().spokenLanguages,
        ),
      { once: true },
    );
  });
}

function persist(state: DictationSettings) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        engine: state.engine,
        inputDeviceId: state.inputDeviceId,
        inputDeviceName: state.inputDeviceName,
        locale: state.locale,
        spokenLanguages: state.spokenLanguages,
      }),
    );
  } catch {
    // Quota or private-mode failures are non-fatal — ignore.
  }
}

const INITIAL = loadInitial();
publish(INITIAL);

export const useDictationSettingsStore = create<DictationSettings & Actions>()(
  mirroredStore("dictationSettings", (set, get) => ({
    ...INITIAL,
    setEngine: (engine) => {
      set({ engine });
      persist(get());
    },
    setInputDevice: (device) => {
      set(device ? parseInputDevice(device.id, device.name) : AUTOMATIC_INPUT);
      persist(get());
    },
    setLocale: (locale) => {
      set({ locale: parseLocale(locale) });
      persist(get());
    },
    setSpokenLanguages: (languages) => {
      set({ spokenLanguages: parseSpokenLanguages(languages) });
      persist(get());
    },
  })),
);

// A change from any window — this one or a mirrored update from another — has
// to reach the synchronous readers, or a recording started right after would
// still use the previous engine, microphone or language.
useDictationSettingsStore.subscribe(publish);

/**
 * Forget the dictation settings — used by account deletion. The app keeps
 * running afterwards, so the live state goes too: left in place, the next
 * change would write the deleted account's choices straight back.
 */
export function clearPersistedDictationSettings(): void {
  useDictationSettingsStore.setState(DEFAULTS);
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
